import { USER_DATA } from '../../constants/applicationConstants';

const EMPTY_AUTHORIZATION = {
  roles: [],
  role_names: [],
  permissions: [],
  permission_names: [],
  modules: {},
};

const toArray = (value) => (Array.isArray(value) ? value : []);

const toRecord = (value) =>
  value && typeof value === 'object' && !Array.isArray(value) ? value : {};

const normalizeBooleanFlag = (value, fallback = true) => {
  if (value === null || value === undefined || value === '') {
    return fallback;
  }

  if (typeof value === 'boolean') {
    return value;
  }

  if (typeof value === 'number') {
    return value !== 0;
  }

  const normalized = String(value).trim().toLowerCase();

  if (['false', '0', 'no', 'inactive'].includes(normalized)) {
    return false;
  }

  if (['true', '1', 'yes', 'active'].includes(normalized)) {
    return true;
  }

  return fallback;
};

export const parseJwt = (token) => {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((char) => `%${`00${char.charCodeAt(0).toString(16)}`.slice(-2)}`)
        .join('')
    );

    return JSON.parse(jsonPayload);
  } catch (_error) {
    return null;
  }
};

export const parseStoredTokenExpiry = (value) => {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  const numericValue = Number(value);
  if (Number.isFinite(numericValue)) {
    return numericValue;
  }

  const parsedDate = Date.parse(String(value));
  if (Number.isFinite(parsedDate)) {
    return Math.floor(parsedDate / 1000);
  }

  return null;
};

export const parseTokenExpirySeconds = (tokens = {}) => {
  const expiresAt =
    tokens?.expires_at || tokens?.expiresAt || tokens?.expiration_date_time;

  if (expiresAt) {
    const parsedDate = Date.parse(String(expiresAt));
    if (Number.isFinite(parsedDate)) {
      return Math.floor(parsedDate / 1000);
    }
  }

  const expiresIn = tokens?.expires_in;
  if (expiresIn === null || expiresIn === undefined || expiresIn === '') {
    return null;
  }

  const parsedAsDate = Date.parse(String(expiresIn));
  if (Number.isFinite(parsedAsDate)) {
    return Math.floor(parsedAsDate / 1000);
  }

  const numericExpiry = Number(expiresIn);
  if (!Number.isFinite(numericExpiry)) {
    return null;
  }

  const nowSeconds = Math.floor(Date.now() / 1000);

  if (numericExpiry > 1000000000) {
    return Math.floor(numericExpiry);
  }

  return nowSeconds + Math.floor(numericExpiry);
};

const normalizeRole = (role) => ({
  role_id: role?.role_id || role?.id || '',
  name: role?.name || '',
  description: role?.description || '',
  active: normalizeBooleanFlag(role?.active, true),
});

const normalizePermission = (permission) => ({
  permission_id: permission?.permission_id || permission?.id || '',
  name: permission?.name || '',
  display_name: permission?.display_name || permission?.displayName || '',
  module: permission?.module || '',
  description: permission?.description || '',
  active: normalizeBooleanFlag(permission?.active, true),
  sources: toArray(permission?.sources),
  direct_assigned: Boolean(permission?.direct_assigned),
  via_roles: toArray(permission?.via_roles),
  via_role_ids: toArray(permission?.via_role_ids),
});

export const normalizeAuthorization = (authorizationSource = {}) => {
  const authorization = toRecord(
    authorizationSource?.authorization || authorizationSource
  );
  const roles = toArray(authorization?.roles).map(normalizeRole);
  const permissions = toArray(authorization?.permissions).map(normalizePermission);
  const roleNames = toArray(authorization?.role_names).filter(Boolean);
  const permissionNames = toArray(authorization?.permission_names).filter(Boolean);
  const modules = Object.entries(
    toRecord(authorization?.modules || authorization?.module_access)
  ).reduce((accumulator, [moduleName, permissionList]) => {
    accumulator[moduleName] = toArray(permissionList).filter(Boolean);
    return accumulator;
  }, {});

  return {
    roles,
    role_names:
      roleNames.length > 0 ? roleNames : roles.map((role) => role.name).filter(Boolean),
    permissions,
    permission_names:
      permissionNames.length > 0
        ? permissionNames
        : permissions.map((permission) => permission.name).filter(Boolean),
    modules,
  };
};

export const normalizeUserPayload = (payload = {}) => {
  const source = toRecord(payload);
  const authorization = normalizeAuthorization({
    roles: source?.roles,
    role_names: source?.role_names,
    permissions: source?.permissions,
    permission_names: source?.permission_names,
    modules: source?.module_access,
    authorization: source?.authorization,
  });
  const username = source?.username || source?.userName || '';
  const emailAddress = source?.email_address || source?.email || '';
  const firstName = source?.first_name || '';
  const lastName = source?.last_name || '';
  const fullName =
    [firstName, lastName].filter(Boolean).join(' ').trim() ||
    source?.full_name ||
    username ||
    emailAddress ||
    'User';

  return {
    ...source,
    id: source?.user_id || source?.id || source?.sub || '',
    user_id: source?.user_id || source?.id || source?.sub || '',
    username,
    userName: username,
    email: emailAddress,
    email_address: emailAddress,
    first_name: firstName,
    last_name: lastName,
    full_name: fullName,
    active: normalizeBooleanFlag(source?.active, true),
    locked: normalizeBooleanFlag(source?.locked, false),
    authorization,
    roles: authorization.roles,
    role_names: authorization.role_names,
    permissions: authorization.permissions,
    permission_names: authorization.permission_names,
    module_access: authorization.modules,
  };
};

export const buildUserAuthObject = (user) => {
  const normalizedUser = normalizeUserPayload(user);

  return {
    user_id: normalizedUser.user_id,
    authorization: normalizedUser.authorization,
    roles: normalizedUser.roles,
    role_names: normalizedUser.role_names,
    permissions: normalizedUser.permissions,
    permission_names: normalizedUser.permission_names,
    module_access: normalizedUser.module_access,
  };
};

export const storeUserData = (user) => {
  if (typeof sessionStorage === 'undefined') {
    return;
  }

  const normalizedUser = normalizeUserPayload(user);
  sessionStorage.setItem(USER_DATA, JSON.stringify(normalizedUser));
};

export const getStoredUserData = () => {
  if (typeof sessionStorage === 'undefined') {
    return null;
  }

  const userJson = sessionStorage.getItem(USER_DATA);
  if (!userJson) {
    return null;
  }

  try {
    return normalizeUserPayload(JSON.parse(userJson));
  } catch (_error) {
    return null;
  }
};

export const getAuthorizationSnapshot = (source) => {
  if (!source) {
    return {
      ...EMPTY_AUTHORIZATION,
      permissionSet: new Set(),
      moduleSet: new Set(),
    };
  }

  const normalizedAuthorization = normalizeAuthorization(
    source?.authorization || {
      roles: source?.roles,
      role_names: source?.role_names,
      permissions: source?.permissions,
      permission_names: source?.permission_names,
      modules: source?.module_access,
    }
  );

  return {
    ...normalizedAuthorization,
    permissionSet: new Set(normalizedAuthorization.permission_names),
    moduleSet: new Set(Object.keys(normalizedAuthorization.modules)),
  };
};

export const hasPermission = (source, permissionName) => {
  if (!permissionName) {
    return true;
  }

  return getAuthorizationSnapshot(source).permissionSet.has(permissionName);
};

export const hasAnyPermission = (source, permissionNames = []) => {
  const requiredPermissions = toArray(permissionNames).filter(Boolean);
  if (requiredPermissions.length === 0) {
    return true;
  }

  const snapshot = getAuthorizationSnapshot(source);
  return requiredPermissions.some((permissionName) =>
    snapshot.permissionSet.has(permissionName)
  );
};

export const hasAllPermissions = (source, permissionNames = []) => {
  const requiredPermissions = toArray(permissionNames).filter(Boolean);
  if (requiredPermissions.length === 0) {
    return true;
  }

  const snapshot = getAuthorizationSnapshot(source);
  return requiredPermissions.every((permissionName) =>
    snapshot.permissionSet.has(permissionName)
  );
};

export const hasModuleAccess = (source, moduleName) => {
  if (!moduleName) {
    return true;
  }

  const snapshot = getAuthorizationSnapshot(source);
  return snapshot.moduleSet.has(moduleName);
};

export const canAccessRoute = (source, route) => {
  if (!route || route?.isauth === false) {
    return true;
  }

  const requiredPermissions = toArray(route?.authPermissions).filter(Boolean);
  const requiredAnyPermissions = toArray(route?.authAnyPermissions).filter(Boolean);
  const requiredModules = toArray(route?.authModules).filter(Boolean);

  if (
    requiredPermissions.length === 0 &&
    requiredAnyPermissions.length === 0 &&
    requiredModules.length === 0
  ) {
    return true;
  }

  if (!source) {
    return false;
  }

  if (!hasAllPermissions(source, requiredPermissions)) {
    return false;
  }

  if (!hasAnyPermission(source, requiredAnyPermissions)) {
    return false;
  }

  if (
    requiredModules.length > 0 &&
    !requiredModules.some((moduleName) => hasModuleAccess(source, moduleName))
  ) {
    return false;
  }

  return true;
};
